import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { tap } from 'rxjs/operators';
import { AutoUnsubscribe, buildProviders, Coerce, FormElementComponent } from '@kalidea/kaligraphi';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
    selector: 'app-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrls: ['./color-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(ColorPickerComponent),
    standalone: false
})
export class ColorPickerComponent extends FormElementComponent<any> {

    @ViewChild('colorPicker', {static: true}) colorPicker: TemplatePortal;

    @Input()
    label = 'Modifier la couleur';

    @Input()
    @Coerce('boolean')
    showColorPreview = false;

    @Input()
    colorsListFunction: (item) => string;

    selectedColor: string = null;

    // @TODO retirer lorsque Kaligraphi sera corrigé
    control = new FormControl();

    /**
     * Overlay reference.
     */
    private overlayRef: OverlayRef;

    @AutoUnsubscribe()
    private overlayBackdropClick: Subscription = Subscription.EMPTY;

    private _colorListSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    constructor(protected cdr: ChangeDetectorRef,
                private overlay: Overlay,
                private elementRef: ElementRef<HTMLElement>) {
        super();
    }

    @Input()
    set colorsList(value: any[]) {
        this._colorListSubject$.next(value);
    }

    get colorsList(): any[] {
        return this._colorListSubject$.value;
    }

    writeValue(value: any): void {
        this.value = value;

        if (this.showColorPreview) {
            this.selectedColor = this.colorsListFunction(value);
            this.cdr.markForCheck();
        }
    }

    private get positionStrategy(): FlexibleConnectedPositionStrategy {
        return this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPush(false)
            .withPositions([
                {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'},
                {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'}
            ]);
    }

    close(): void {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
    }

    open($event: MouseEvent = null): void {
        // stop propagation of this event
        if ($event) {
            $event.stopPropagation();
        }

        if (!this.disabled && !this.overlayRef || !this.overlayRef.hasAttached()) {
            this.getOverlayRef().attach(this.colorPicker);
        }
    }

    private getOverlayRef(): OverlayRef {
        if (!this.overlayRef) {
            this.overlayRef = this.overlay.create({
                positionStrategy: this.positionStrategy,
                scrollStrategy: this.overlay.scrollStrategies.close({threshold: 300}),
                hasBackdrop: true,
                width: '240px',
            });

            if (this.overlayBackdropClick) {
                this.overlayBackdropClick.unsubscribe();
            }

            // watch escape key
            this.overlayBackdropClick = this.overlayRef.backdropClick()
                .pipe(
                    tap(() => this.close())
                ).subscribe();
        }

        return this.overlayRef;
    }

    selectColor(item: any): void {
        this.close();
        this.notifyUpdate(item);

        if(this.showColorPreview) {
            this.selectedColor = this.colorsListFunction(item);
            this.cdr.markForCheck();
        }
    }
}
